import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── «בדיקת נתונים» — data audit agent (ADR-0035, Turbo's kupotApi.dataAudit) ── */
export type AuditGroupDto = { key: string; label: string; count: number; amount: number };

export type AuditCheckDto = {
  code: string;
  area: string;
  areaLabel: string;
  title: string;
  explain: string;
  fixLabel: string | null;
  risk: "safe" | "review";
  impact: number;
  moneyCheck: boolean;
  allOrNothing: boolean;
  count: number;
  truncated: boolean;
  /** shekels */
  amount: number;
  recommended: number;
  recommendedAmount: number;
  blocked: number;
  fixable: number;
  groups: AuditGroupDto[];
  facts: Record<string, unknown> | null;
  canFix: boolean;
  undoable: boolean;
  at: string | null;
  error?: string;
};

export type AuditItemDto = {
  id: string;
  entity: string;
  label: string;
  detail: string;
  date: string | null;
  amountShekels: number | null;
  path: string;
  selectable: boolean;
  /** the agent recommends fixing it */
  preselect: boolean;
  /** a warning for this row */
  note: string | null;
};

export type AuditItemsDto = {
  code: string;
  title: string;
  fixLabel: string | null;
  canFix: boolean;
  check: AuditCheckDto;
  total: number;
  sum: number;
  items: AuditItemDto[];
};

export type AuditItemsQuery = {
  offset?: number;
  limit?: number;
  text?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  noted?: boolean;
  recommended?: boolean;
  selectable?: boolean;
};

export type AuditFixDto = {
  id: string;
  checkCode: string;
  code: string;
  title: string;
  fixLabel: string | null;
  label: string;
  status: "applied" | "undone" | "final" | string;
  undone: boolean;
  count: number;
  amount: number;
  failed: Array<{ id: string; label: string; error: string }>;
  items: Array<{ id: string; entity: string; label: string; amount: number | null; date: string | null }>;
  userName: string | null;
  createdAt: string;
  at: string;
  undoneAt: string | null;
  canUndo: boolean;
  skipped?: number;
};

export type AuditStateDto = { checks: AuditCheckDto[] | null; fixes: AuditFixDto[] };

export type AuditFixActionDto = {
  type: "fix";
  code: string;
  title: string;
  ids?: string[];
  recommended: boolean;
  count: number;
  amount: number;
  label: string;
  risk: "safe" | "review";
  auto?: boolean;
};
export type AuditChatActionDto = AuditFixActionDto | { type: "refresh_findings" };
export type AuditChatDto = { reply: string; steps: string[]; actions: AuditChatActionDto[] };
export type AuditChatTurn = { role: "user" | "assistant"; text: string };

const LONG = { timeout: 180_000 };

export const aiDataAuditApi = {
  /** what this user's last run found (checks null = never run) + the fix history */
  state(): Promise<AuditStateDto> {
    return wrap(async () => (await httpClient.get<{ item: AuditStateDto }>("/ai/data-audit")).data.item);
  },
  run(codes?: string[]): Promise<AuditCheckDto[]> {
    return wrap(async () => (await httpClient.post<{ items: AuditCheckDto[] }>("/ai/data-audit/run", codes?.length ? { codes } : {}, LONG)).data.items);
  },
  items(code: string, query: AuditItemsQuery = {}): Promise<AuditItemsDto> {
    return wrap(
      async () =>
        (await httpClient.get<{ item: AuditItemsDto }>(`/ai/data-audit/checks/${encodeURIComponent(code)}`, { params: query })).data.item,
    );
  },
  fix(body: { code: string; ids?: string[]; recommended?: boolean }): Promise<{ fix: AuditFixDto; check: AuditCheckDto }> {
    return wrap(async () => (await httpClient.post<{ item: { fix: AuditFixDto; check: AuditCheckDto } }>("/ai/data-audit/fix", body, LONG)).data.item);
  },
  fixes(): Promise<AuditFixDto[]> {
    return wrap(async () => (await httpClient.get<{ items: AuditFixDto[] }>("/ai/data-audit/fixes")).data.items);
  },
  undo(id: string): Promise<{ id: string; restored: number; changed: number; fix: AuditFixDto | null }> {
    return wrap(
      async () =>
        (
          await httpClient.post<{ item: { id: string; restored: number; changed: number; fix: AuditFixDto | null } }>(
            `/ai/data-audit/fixes/${id}/undo`,
            {},
            LONG,
          )
        ).data.item,
    );
  },
  chat(message: string, history: AuditChatTurn[]): Promise<AuditChatDto> {
    return wrap(
      async () =>
        (await httpClient.post<{ item: AuditChatDto }>("/ai/data-audit/chat", { message, history: history.slice(-16) }, LONG)).data.item,
    );
  },
};
